import { useCallback, useState, type CSSProperties } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ApiError, api } from '../../lib/api/client.js';
import type { LearnLesson, LearningSessionResponse } from '../../lib/api/types.js';
import { LoadingState } from '../../ui/components/LoadingState/LoadingState.tsx';
import { useAsyncData } from '../../ui/components/LoadingState/useAsyncData.ts';
import { OfflineState } from '../../ui/components/OfflineState/OfflineState.tsx';
import { Badge } from '../../ui/primitives/Badge.tsx';
import { Button } from '../../ui/primitives/Button.tsx';
import { Icon } from '../../ui/primitives/icon/index.ts';
import { Text } from '../../ui/primitives/Text.tsx';
import { lessonState, countCompleted, exerciseCount } from './progress.ts';
import { exerciseTypeLabel } from './exercise-labels.ts';
import styles from './content.module.css';

/**
 * `/` — Aprenda, the spine. "Where am I in the book?", and nothing else.
 *
 * One continuous column, in order, with the learner's position marked on it: a
 * line that is grad/progress behind them and the muted track ahead, and a node
 * per Lesson threaded on it.
 *
 * THE LESSON SCREEN IS GONE, ABSORBED HERE. A Lesson opens IN PLACE — its
 * title, how far into it you are (`03 — 08`, position, never a percentage,
 * § 14), what kind of work it holds, and the one action that continues it —
 * because a Lesson read on a screen of its own is a list of Exercises with no
 * answer to "and then what".
 *
 * AND OPENING ONE IS NOT A NAVIGATION. It used to be: every node carried an
 * address ending in a uuid, and pressing one pushed a history entry whose
 * only difference from this screen was which node was expanded. That put a
 * thirty-six character identifier in front of the learner, made "back" mean
 * "collapse", and gave the same screen as many addresses as there are
 * Lessons. The open Lesson is now what it always was in substance — a piece
 * of state on the spine — so `/` is the whole of the spine's address and
 * nothing the learner reads there is machine-shaped.
 *
 * EVERY FIGURE HERE IS A COUNT THE SERVER ACTUALLY REPORTS (§ 14). Nothing on
 * this screen approximates one, and no accuracy or recognition figure is
 * derived from the counts that are here.
 */

interface Content {
  lessons: readonly LearnLesson[];
  session: LearningSessionResponse | null;
}

const EMPTY_CONTENT: Content = { lessons: [], session: null };

export function LessonsPage() {
  const navigate = useNavigate();
  /*
   * Which Lesson the learner has pressed open, if any. `null` is not "none
   * open" — it is "they have not said", and the fallback chain below answers
   * with the cursor, which is the Lesson a learner who says nothing means.
   *
   * It starts from whatever navigation state carried in, which is how
   * `/revisao` hands the spine back the Lesson the learner was just reading:
   * they opened a finished node to see their answers, and coming back should
   * put them in front of that node rather than wherever the cursor happens to
   * stand.
   */
  const arrivedOn = (useLocation().state as { lessonId?: string } | null)?.lessonId ?? null;
  const [requestedLessonId, setRequestedLessonId] = useState<string | null>(arrivedOn);

  const loadContent = useCallback(async (): Promise<Content> => {
    const [content, current] = await Promise.all([
      api.lessons(),
      // 404 is the ordinary first-visit state, not a failure: this learner
      // has simply never started. Any other failure is a real one and falls
      // through to the catch below.
      api.currentSession().catch((error: unknown) => {
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
      }),
    ]);
    return { lessons: content.lessons, session: current };
  }, []);

  const { status, data, showLoading, reload } = useAsyncData(loadContent, EMPTY_CONTENT);
  const { lessons, session } = data;

  if (status === 'loading') return showLoading ? <LoadingState label="Carregando suas lições" /> : null;

  if (status === 'unavailable') {
    return (
      <div className={styles.page}>
        <OfflineState
          title="Não foi possível carregar suas lições"
          onRetry={() => reload({ force: true })}
        />
      </div>
    );
  }

  const totalExercises = lessons.reduce((sum, lesson) => sum + exerciseCount(lesson), 0);
  const completedExercises = lessons.reduce((sum, lesson) => sum + countCompleted(lesson), 0);
  const completedLessons = lessons.filter((lesson) => lesson.completed).length;
  const hasContent = totalExercises > 0;
  const allComplete = hasContent && completedExercises === totalExercises;
  const position = session?.position ?? null;
  const started = position !== null;

  /*
   * Which Lesson is open, in order of who gets to decide:
   *
   *  1. the one the learner pressed, because they asked for it out loud;
   *  2. the one the cursor sits in, because that is where the work is;
   *  3. the first, because the spine must mark somewhere as "here" even for a
   *     learner who has never started — it is the Lesson "Começar" opens.
   */
  const openLesson =
    (requestedLessonId
      ? (lessons.find((lesson) => lesson.id === requestedLessonId) ?? null)
      : null) ??
    (position ? (lessons.find((lesson) => lesson.id === position.lesson.id) ?? null) : null) ??
    (lessons[0] ?? null);

  // A Lesson an administrator removed while the learner had it open is a
  // missing Lesson, not a broken screen (FR-062) — the spine still renders,
  // falls back to the cursor, and the line above it says why the Lesson they
  // opened is no longer on it.
  const requestedIsMissing =
    requestedLessonId !== null && !lessons.some((lesson) => lesson.id === requestedLessonId);

  const openLessonIndex = openLesson ? lessons.indexOf(openLesson) + 1 : null;
  const positionLine =
    openLessonIndex !== null && lessons.length > 0
      ? `${String(completedExercises).padStart(2, '0')} — ${String(totalExercises).padStart(2, '0')} exercícios · lição ${openLessonIndex} de ${lessons.length}`
      : null;

  return (
    <div className={styles.page}>
      <header className={styles.greeting}>
        <Text as="h1" variant="display-xl">
          O caminho
        </Text>

        {!hasContent ? (
          <Text as="p" variant="body" tone="muted">
            Ainda não há lições publicadas. Assim que houver, elas aparecem aqui.
          </Text>
        ) : requestedIsMissing ? (
          <Text as="p" variant="body-sm" tone="muted">
            A lição que você abriu não existe mais. Ela pode ter sido removida.
          </Text>
        ) : positionLine ? (
          <Text variant="numeric-inline" tone="muted">
            {positionLine}
          </Text>
        ) : null}
      </header>

      {hasContent ? (
        <ol
          className={styles.spine}
          aria-label="Seu caminho nas lições"
          /*
            The cord runs grad/progress as far as the learner has come and the
            muted track from there on. It counts LESSONS, not Exercises,
            because Lessons are what the cord threads: the nodes on it are
            Lessons, so a length measured in anything else would stop short of
            or run past the very node it is describing. The line above the
            spine is where the exercise count is answered.
          */
          style={
            { '--spine-progress': `${(completedLessons / lessons.length) * 100}%` } as CSSProperties
          }
        >
          {lessons.map((lesson) => {
            const state = lessonState(lesson, position?.lesson.id ?? null);
            const done = countCompleted(lesson);
            const total = exerciseCount(lesson);
            const isOpen = lesson.id === openLesson?.id && !allComplete;
            // Practice always resumes where the learner actually is, so an open
            // Lesson that is not the current one says so rather than offering
            // to start somewhere a learner cannot be sent (FR-070).
            const isCurrent = lesson.id === position?.lesson.id;
            const exerciseType = lesson.exercises[0]?.exerciseType
              ? exerciseTypeLabel(lesson.exercises[0].exerciseType)
              : null;

            return (
              <li key={lesson.id} className={styles.spineRow} data-state={isOpen ? 'active' : state}>
                {/* The node on the line. */}
                <span className={styles.node} data-state={state} aria-hidden="true">
                  {state === 'completed' ? (
                    <Icon name="check" size={20} />
                  ) : state === 'active' ? (
                    <span className={styles.nodeNow} />
                  ) : null}
                </span>

                {isOpen ? (
                  /*
                    The open Lesson — the work absorbed inline. Its own quiet
                    surface carries the grad/brasil border as a sign that this is
                    where the reader is (§ 6: a gradient outline is a sign of
                    present interactivity), the segmented bar with the frontier on
                    the Exercise the cursor is on, and the single action that
                    continues the run.
                  */
                  <section
                    className={styles.spineFocus}
                    aria-labelledby={`lesson-title-${lesson.id}`}
                  >
                    <div className={styles.spineFocusTitle}>
                      <Text as="h2" id={`lesson-title-${lesson.id}`} variant="heading">
                        {lesson.title}
                      </Text>
                      {lesson.completed ? (
                        <Badge tone="success" icon="check">
                          Concluída
                        </Badge>
                      ) : isCurrent ? (
                        <Badge tone="accent">Em andamento</Badge>
                      ) : null}
                    </div>

                    <div className={styles.spineRun}>
                      <div
                        className={styles.segments}
                        role="img"
                        aria-label={`Você está no exercício ${Math.min(done + 1, total)} de ${total}`}
                      >
                        {lesson.exercises.map((exercise, exerciseIndex) => (
                          <span
                            key={exercise.id}
                            className={styles.segment}
                            data-state={
                              exerciseIndex < done
                                ? 'done'
                                : exerciseIndex === done
                                  ? 'current'
                                  : 'todo'
                            }
                            /*
                              Each done segment is told its place in the done
                              RUN, because the run carries one continuous
                              grad/progress between them all rather than a
                              gradient each. The arithmetic is the
                              stylesheet's; these are the two numbers it
                              cannot know.
                            */
                            style={
                              exerciseIndex < done
                                ? ({
                                    '--segment-index': exerciseIndex,
                                    '--segment-count': done,
                                  } as CSSProperties)
                                : undefined
                            }
                          />
                        ))}
                      </div>
                      <Text variant="numeric-inline">
                        {String(done).padStart(2, '0')} — {String(total).padStart(2, '0')}
                      </Text>
                    </div>

                    {exerciseType ? (
                      <Text as="p" variant="body-sm" tone="muted">
                        {exerciseType} · {total} {total === 1 ? 'exercício' : 'exercícios'}
                      </Text>
                    ) : null}

                    {/*
                      A FINISHED LESSON IS NOT WHERE PRACTICE GOES.

                      Practice always resumes at the learner's ONE position,
                      so "Ir para a prática" on a Lesson that is not the
                      current one sends them somewhere else entirely — which
                      on a Lesson they have finished reads as a broken button,
                      and on a Lesson whose next Exercise cannot be opened
                      reads as a broken product. A finished Lesson says what
                      it can actually offer instead: look at what you
                      answered, or do it again. Both are about THIS Lesson,
                      which is what the learner asked about by opening it.
                    */}
                    {!isCurrent && position && !lesson.completed ? (
                      <Text as="p" variant="body-sm" tone="muted">
                        Sua prática continua em <strong>{position.lesson.title}</strong>, onde você
                        parou.
                      </Text>
                    ) : null}

                    <div className={styles.spineFocusAction}>
                      {lesson.completed ? (
                        <Button
                          icon="rotate-ccw"
                          onClick={() => navigate('/revisao', { state: { lessonId: lesson.id } })}
                        >
                          Ver suas respostas
                        </Button>
                      ) : (
                        <Button icon="play" onClick={() => navigate('/praticar')}>
                          {!isCurrent
                            ? 'Ir para a prática'
                            : started
                              ? `Continuar no exercício ${Math.min(done + 1, total)}`
                              : 'Começar'}
                        </Button>
                      )}
                    </div>
                  </section>
                ) : (
                  /*
                    A quiet node: done (green, checked) or still to come (hollow).
                    Opening it opens it HERE, on this page, at its own address; it
                    never starts practice, because a learner has one position and
                    one run.
                  */
                  <button
                    type="button"
                    className={styles.spineRowBody}
                    data-state={state}
                    onClick={() => {
                      // A finished node has one thing left in it, and the
                      // spine no longer pretends otherwise: what you
                      // answered, and the way to run it again. That is the
                      // one node that leaves this screen, and it hands the
                      // review the Lesson it is about rather than spelling
                      // it into the address. Everything else opens in place,
                      // on the spine, without moving the learner at all.
                      if (lesson.completed) {
                        navigate('/revisao', { state: { lessonId: lesson.id } });
                        return;
                      }
                      setRequestedLessonId(lesson.id);
                    }}
                  >
                    <Text variant="subheading">{lesson.title}</Text>
                    <Text variant="numeric-inline" tone="muted">
                      {String(done).padStart(2, '0')} — {String(total).padStart(2, '0')}
                    </Text>
                  </button>
                )}
              </li>
            );
          })}
        </ol>
      ) : null}

      {hasContent && allComplete ? (
        /*
          Everything done. There is no "position" left to mark on the spine, so
          the spine gives way to the completion state § 13 asks for: a summary
          and the single action that keeps the ear moving.
        */
        <section className={styles.spineComplete} aria-live="polite">
          <Text as="h2" variant="heading">
            Tudo concluído
          </Text>
          <Text as="p" variant="body" tone="muted">
            Você concluiu {totalExercises} {totalExercises === 1 ? 'exercício' : 'exercícios'} em{' '}
            {completedLessons} {completedLessons === 1 ? 'lição' : 'lições'}. Pratique de novo quando
            quiser.
          </Text>
          {/*
            "Praticar novamente" used to send the learner to `/praticar`, which
            with every Exercise complete has nothing to show them but the
            completed-content state they are already reading. Now there is
            something a completed catalogue can actually offer, and it is per
            Lesson: open one, see what you answered, run it again.
          */}
          {/*
            No Lesson named: `/revisao` arriving with nothing to go on reviews
            the last Lesson finished, which with every Lesson finished is this
            control's whole meaning.
          */}
          <Button icon="rotate-ccw" onClick={() => navigate('/revisao')}>
            Rever a última lição
          </Button>
        </section>
      ) : null}
    </div>
  );
}
